import type { FunctionComponent } from "react";
import {
	type ColumnDef,
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	getFilteredRowModel,
	type VisibilityState,
	type ColumnFiltersState,
} from "@tanstack/react-table";
import React, { useEffect } from "react";
import Spinner from "../../../components/spinner.components";
import { DataTableComponents } from "../../../components/tables/table.components";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { useToast } from "../../../hooks/use-toast";
import type { IPayoutTransactionMinimal } from "../../../types/models/payout-transaction.modals.types";
import { handleHttpError } from "../../../utils/utils";
import { DateTime } from "luxon";
import { Button } from "../../../components/ui/button";
import { Link } from "react-router-dom";
import {
	type INotificationExtended,
	useGetPastNotificationsAdminHttp,
} from "../../../hooks/http/admin/notifications/get-past-notifications.admin.http";

export const CommunicationAdminPartial: FunctionComponent = () => {
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
		[],
	);
	const [columnVisibility, setColumnVisibility] =
		React.useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = React.useState({});
	const [data, setData] = React.useState<INotificationExtended[]>([]);
	const [, setPageNo] = React.useState(1);
	const { toast } = useToast();
	const [paginationData, setPaginationData] = React.useState<IPagination>();
	const {
		execute: getPastNotificationsHttp,
		fetching,
		cancel: cancelGetPastNotificationsHttp,
	} = useGetPastNotificationsAdminHttp();

	async function getPastNotifications(
		data: INotificationExtended[],
		pageNo: number,
	) {
		cancelGetPastNotificationsHttp();
		const response = await getPastNotificationsHttp({
			body: null,
			queryParams: {
				page: pageNo,
				size: 7,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setData([...data, ...response.data.broadcasts]);
		setPaginationData(response.data.pagination);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getPastNotifications(data, 1);
	}, []);

	const columns: ColumnDef<INotificationExtended>[] = [
		{
			accessorKey: "title",
			header: "Message Title",
			cell: ({ row }) => {
				const { title } = row.original;

				return <div className="py-2 font-semibold tracking-wider">{title}</div>;
			},
		},
		{
			accessorKey: "metrics",
			header: "Sent count",
			cell: ({ row }) => {
				const { metrics } = row.original;

				return (
					<div className="font-semibold tracking-wider">
						{metrics.notificationCount}
					</div>
				);
			},
		},

		{
			accessorKey: "sentAt",
			header: "Sent at",
			cell: ({ row }) => {
				const { sentAt } = row.original;

				return (
					<div className="font-semibold tracking-wider">
						{sentAt
							? DateTime.fromISO(new Date(sentAt).toISOString()).toFormat(
									"dd-MM-yyyy",
								)
							: "-"}
					</div>
				);
			},
		},

		// {
		// 	accessorKey: "id",
		// 	header: "",
		// 	cell: () => {
		// 		return (
		// 			<div className="flex justify-end items-center gap-4">
		// 				<Button
		// 					disabled
		// 					className="bg-tertiarybuttonbg border border-border font-semibold"
		// 				>
		// 					View details
		// 				</Button>
		// 			</div>
		// 		);
		// 	},
		// },
	];

	const table = useReactTable({
		data,
		columns,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		state: {
			columnFilters,
			columnVisibility,
			rowSelection,
		},
		manualPagination: true,
	});

	return (
		<div className="flex flex-col gap-16 px-10 pt-16">
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
			<div
				id="getPastNotifications"
				onClick={() => {
					getPastNotifications([], 1);
				}}
				className="hidden"
			/>
			<div className="flex justify-between items-center">
				<h1 className="font-semibold text-2xl">Communication</h1>
				<Link to="/admin/communication/new">
					<Button className="flex items-center gap-1 bg-primarybuttonbg px-[38px] py-[28px] border border-border rounded-lg font-semibold text-base text-primarybuttontext tracking-wider">
						<span className="text-2xl">+</span> New communication
					</Button>
				</Link>
			</div>
			{data.length <= 0 && fetching ? (
				<div className="flex justify-center items-center pt-20 w-full">
					<Spinner />
				</div>
			) : (
				<DataTableComponents
					table={table as ReturnType<typeof useReactTable>}
					columns={columns as ColumnDef<IPayoutTransactionMinimal>[]}
					setPageNo={() => {
						setPageNo((prev) => {
							const nextPage = prev + 1;
							getPastNotifications(data, nextPage);
							return nextPage;
						});
					}}
					loading={fetching}
					tableType="notification"
					paginationData={paginationData}
				/>
			)}
		</div>
	);
};
